import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Search, ArrowUp, ArrowDown, GripVertical, Eye } from "lucide-react";
import { toast } from "sonner";
import { http, FALLBACK_PHOTO, absoluteUrl } from "../lib/api";
import { Logo } from "../components/Brand";
import { useAuth } from "../context/AuthContext";
import AthleteForm from "./AthleteForm";

const DEMO_SLUGS = ["marcus-ellis", "jalen-brooks", "devon-carter"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get("/athletes", { params: { include_unpublished: true } });
      setAthletes(data);
    } catch {
      // Keep whatever is already on screen instead of blanking the roster.
      toast.error("Could not refresh the roster — your athletes are safe, try again.");
    }
  };

  useEffect(() => {
    document.title = "Admin | Faith Up Athletes";
    load();
  }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.name}? This cannot be undone.`)) return;
    try {
      await http.delete(`/athletes/${a.athlete_id}`);
      toast.success(`${a.name} deleted`);
    } catch (err) {
      toast.error(err.sessionExpired ? "Session expired — sign in again" : "Delete failed");
    }
    load();
  };

  const filtered = athletes.filter((a) =>
    a.name.toLowerCase().includes(search.toLowerCase())
  );

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= athletes.length) return;
    await commitOrder(index, target);
  };

  const commitOrder = async (from, to) => {
    const next = [...athletes];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    setAthletes(next);
    try {
      await http.post("/athletes/reorder", { athlete_ids: next.map((a) => a.athlete_id) });
    } catch (err) {
      toast.error(err.sessionExpired ? "Session expired — sign in again" : "Could not save order");
      load();
    }
  };

  const onDrop = (to) => {
    if (dragIndex === null || dragIndex === to) return setDragIndex(null);
    commitOrder(dragIndex, to);
    setDragIndex(null);
  };

  if (showForm)
    return (
      <AthleteForm
        athlete={editing}
        onClose={() => {
          setShowForm(false);
          setEditing(null);
        }}
        onSaved={() => {
          setShowForm(false);
          setEditing(null);
          load();
        }}
      />
    );

  return (
    <div className="min-h-screen lg:flex" data-testid="admin-dashboard">
      <aside className="lg:w-72 bg-[#061224] border-b lg:border-b-0 lg:border-r border-white/10 p-6 lg:min-h-screen">
        <Logo className="h-16" />
        <div className="mt-10 space-y-2">
          <p className="label-mono">Signed in</p>
          <p className="text-sm font-semibold truncate">{user?.name}</p>
          <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
        </div>
        <div className="mt-10 flex flex-col gap-3">
          <Link to="/" data-testid="admin-view-site-link" className="label-mono hover:text-[#FF5A00]">
            View public site
          </Link>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => {
              await logout();
              window.location.replace("/login");
            }}
            className="label-mono text-left hover:text-[#FF5A00]"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 sm:p-10">
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end justify-between">
          <div>
            <p className="label-mono text-[#FF5A00]">Management</p>
            <h1 className="font-display text-5xl sm:text-6xl leading-none">Athlete Roster</h1>
          </div>
          <button
            data-testid="add-athlete-btn"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="clip-slant bg-[#FF5A00] hover:bg-[#FF7B33] transition-colors px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] flex items-center gap-2 justify-center"
          >
            <Plus size={16} /> Add Athlete
          </button>
        </div>

        {athletes.some((a) => DEMO_SLUGS.includes(a.slug)) && (
          <button
            data-testid="clear-demos-btn"
            onClick={async () => {
              if (!window.confirm("Remove the 3 sample demo profiles?")) return;
              const { data } = await http.delete("/athletes/demo/clear");
              toast.success(`${data.deleted} demo profile(s) removed`);
              load();
            }}
            className="mt-6 border border-white/20 hover:border-red-500 hover:text-red-500 transition-colors px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-2"
          >
            <Trash2 size={14} /> Clear demo athletes
          </button>
        )}

        <div className="relative mt-10 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search athletes"
            className="w-full bg-[#111111] border-b-2 border-white/10 focus:border-[#FF5A00] outline-none py-3 pl-10 pr-4 text-sm"
          />
        </div>

        <div className="mt-8 border border-white/10">
          {filtered.length === 0 ? (
            <p className="p-10 label-mono" data-testid="admin-empty-state">
              No athletes yet — add your first profile.
            </p>
          ) : (
            filtered.map((a, i) => (
              <div
                key={a.athlete_id}
                data-testid={`admin-row-${a.slug}`}
                draggable={!search}
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                onDragEnd={() => setDragIndex(null)}
                className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.03] ${
                  dragIndex === i ? "opacity-40" : ""
                } ${!search ? "cursor-grab active:cursor-grabbing" : ""}`}
              >
                {!search && (
                  <div className="flex sm:flex-col items-center gap-1">
                    <GripVertical size={16} className="text-zinc-600 hidden sm:block" />
                    <button
                      data-testid={`admin-move-up-${a.slug}`}
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      title="Move up"
                      className="p-1.5 border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00] disabled:opacity-25 disabled:hover:border-white/20"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      data-testid={`admin-move-down-${a.slug}`}
                      onClick={() => move(i, 1)}
                      disabled={i === filtered.length - 1}
                      title="Move down"
                      className="p-1.5 border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00] disabled:opacity-25 disabled:hover:border-white/20"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                )}
                <img
                  src={absoluteUrl(a.photo_url || FALLBACK_PHOTO)}
                  alt=""
                  className="w-16 h-20 object-cover bg-[#061224]"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-2xl leading-none">
                    {a.jersey ? `#${String(a.jersey).replace("#", "")} ` : ""}
                    {a.name}
                  </p>
                  <p className="text-xs text-zinc-400 mt-1 truncate">
                    {[a.position, a.team].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <span
                  data-testid={`admin-views-${a.slug}`}
                  title="Profile views"
                  className="hidden sm:flex items-center gap-1.5 text-zinc-400 shrink-0"
                >
                  <Eye size={14} />
                  <span className="font-display text-lg leading-none">{a.views ?? 0}</span>
                </span>
                <span
                  className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 border ${
                    a.published ? "border-[#FF5A00] text-[#FF5A00]" : "border-white/20 text-zinc-400"
                  }`}
                >
                  {a.published ? "Live" : "Draft"}
                </span>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/athletes/${a.slug}`}
                    data-testid={`admin-view-${a.slug}`}
                    className="p-2 border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00]"
                    title="View profile"
                  >
                    <ExternalLink size={16} />
                  </Link>
                  <button
                    data-testid={`admin-edit-${a.slug}`}
                    onClick={() => {
                      setEditing(a);
                      setShowForm(true);
                    }}
                    className="p-2 border border-white/20 hover:border-[#FF5A00] hover:text-[#FF5A00]"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`admin-delete-${a.slug}`}
                    onClick={() => remove(a)}
                    className="p-2 border border-white/20 hover:border-red-500 hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
